import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { useAuth } from '../auth/auth-context';
import { chargerEspaces, chargerMonMembre } from './annuaire';
import type { Member, Workspace } from './types';

type EspaceContextValue = {
  espaces: Workspace[];
  espaceCourant: Workspace | null;
  choisirEspace: (id: string) => void;
  /** Recharge la liste des espaces, par exemple après la création d'un espace privé. */
  rafraichirEspaces: () => Promise<void>;
  monMembre: Member | null;
  /** Remplace la fiche en mémoire, par exemple après un changement de nom. */
  majMonMembre: (membre: Member) => void;
  /** Faux tant qu'on ne sait pas encore si la personne a une fiche d'annuaire. */
  membreResolu: boolean;
  peutAdministrer: boolean;
  chargement: boolean;
  erreur: string | null;
};

const EspaceContext = createContext<EspaceContextValue | null>(null);
const CLE_ESPACE = 'alba.espace';

export function EspaceProvider({ children }: PropsWithChildren) {
  const { session } = useAuth();
  const [espaces, setEspaces] = useState<Workspace[]>([]);
  const [espaceId, setEspaceId] = useState<string | null>(null);
  const [monMembre, setMonMembre] = useState<Member | null>(null);
  const [membreResolu, setMembreResolu] = useState(false);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const chargerListeEspaces = useCallback(
    async (conserverSelection: boolean) => {
      const liste = await chargerEspaces();
      setEspaces(liste);
      const memorise = conserverSelection ? (espaceId ?? localStorage.getItem(CLE_ESPACE)) : null;
      const choisi = liste.find((espace) => espace.id === memorise) ?? liste[0] ?? null;
      setEspaceId(choisi?.id ?? null);
      setErreur(null);
    },
    [espaceId],
  );

  useEffect(() => {
    if (!session) {
      setEspaces([]);
      setEspaceId(null);
      setMonMembre(null);
      setChargement(false);
      return;
    }

    let annule = false;
    setChargement(true);
    chargerEspaces()
      .then((liste) => {
        if (annule) return;
        setEspaces(liste);
        const memorise = localStorage.getItem(CLE_ESPACE);
        const choisi = liste.find((espace) => espace.id === memorise) ?? liste[0] ?? null;
        setEspaceId(choisi?.id ?? null);
        setErreur(null);
      })
      .catch((cause: Error) => {
        if (!annule) setErreur(cause.message);
      })
      .finally(() => {
        if (!annule) setChargement(false);
      });

    return () => {
      annule = true;
    };
  }, [session]);

  const rafraichirEspaces = useCallback(async () => {
    try {
      await chargerListeEspaces(true);
    } catch (cause) {
      setErreur((cause as Error).message);
    }
  }, [chargerListeEspaces]);

  useEffect(() => {
    if (!espaceId) {
      setMonMembre(null);
      setMembreResolu(false);
      return;
    }
    let annule = false;
    setMembreResolu(false);
    chargerMonMembre(espaceId)
      .then((membre) => {
        if (!annule) setMonMembre(membre);
      })
      .catch(() => {
        if (!annule) setMonMembre(null);
      })
      .finally(() => {
        if (!annule) setMembreResolu(true);
      });
    return () => {
      annule = true;
    };
  }, [espaceId]);

  const majMonMembre = useCallback((membre: Member) => setMonMembre(membre), []);

  const choisirEspace = useCallback((id: string) => {
    setEspaceId(id);
    localStorage.setItem(CLE_ESPACE, id);
  }, []);

  const espaceCourant = useMemo(
    () => espaces.find((espace) => espace.id === espaceId) ?? null,
    [espaces, espaceId],
  );

  // La règle est appliquée par la base ; ici elle ne sert qu'à masquer ce qui
  // ne servirait à rien d'afficher.
  const peutAdministrer =
    monMembre?.role === 'direction' ||
    monMembre?.role === 'presidence' ||
    (Boolean(espaceCourant?.owner_user_id) && espaceCourant?.owner_user_id === session?.user.id);

  const value = useMemo(
    () => ({
      espaces,
      espaceCourant,
      choisirEspace,
      rafraichirEspaces,
      monMembre,
      majMonMembre,
      membreResolu,
      peutAdministrer,
      chargement,
      erreur,
    }),
    [
      espaces,
      espaceCourant,
      choisirEspace,
      rafraichirEspaces,
      monMembre,
      majMonMembre,
      membreResolu,
      peutAdministrer,
      chargement,
      erreur,
    ],
  );

  return <EspaceContext.Provider value={value}>{children}</EspaceContext.Provider>;
}

export function useEspace() {
  const context = useContext(EspaceContext);
  if (!context) throw new Error('useEspace doit être utilisé dans EspaceProvider.');
  return context;
}
